import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography, Button, IconButton, Chip, Alert,
  LinearProgress, Tooltip, CircularProgress, Grid, Fade, Grow,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import {
  UploadFile as FileIcon,
  CloudUpload as CloudIcon,
  Delete as DeleteIcon,
  CheckCircle as DoneIcon,
  WarningAmber as WarnIcon,
  ErrorOutline as ErrorIcon,
  ExpandMore as ExpandIcon,
  PlayArrow as RunIcon,
  Insights as AnalyzeIcon,
  Replay as RedoIcon,
  Description as SheetIcon,
  AutoAwesome as MagicIcon,
  RocketLaunch as RocketIcon,
  Folder as FolderIcon,
  Inventory as PackageIcon,
  Groups as StudentsIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// ImportScoresPage — drag-and-drop bulk importer. The page itself
// accepts drops anywhere; the hero zone is purely visual encouragement.
// No pre-selection of class / term / exam — every metadata lives in
// the Excel banner and is read on the server.

const ACCEPTED = ['.xls', '.xlsx', '.xml'];

// Subtle floating animation for the cloud icon in the hero — gives
// the empty state some life without being noisy.
const floatAnim = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
`;
const pulseRing = keyframes`
  0%   { transform: scale(0.95); opacity: 0.6; }
  70%  { transform: scale(1.4);  opacity: 0;   }
  100% { transform: scale(1.4);  opacity: 0;   }
`;

export default function ImportScoresPage() {
  const theme = useTheme();

  // File queue
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);   // page-wide overlay

  // Upload state
  const [busy, setBusy] = useState(false);           // 'analyzing' | 'committing'
  const [progress, setProgress] = useState(0);
  const [analysis, setAnalysis] = useState(null);
  const [committed, setCommitted] = useState(null);

  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false);

  // Workflow step indicator: 1 = drop, 2 = analyze, 3 = import
  const currentStep = committed ? 3
    : analysis ? (analysis.hasErrors ? 1 : 2)
    : files.length > 0 ? 2
    : 1;

  // ── file queue helpers ──────────────────────────────────────
  const addFiles = useCallback((list) => {
    if (!list || list.length === 0) return;
    const next = [];
    let skipped = 0;
    for (const f of list) {
      const ext = ('.' + f.name.split('.').pop()).toLowerCase();
      if (!ACCEPTED.includes(ext)) { skipped += 1; continue; }
      next.push({
        id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        file: f, name: f.name, size: f.size,
      });
    }
    if (next.length === 0) {
      swal.warning('Only .xls / .xlsx / .xml files are accepted');
      return;
    }
    if (skipped > 0) {
      swal.info(`Added ${next.length} file(s); skipped ${skipped} unsupported.`);
    }
    setFiles(prev => [...prev, ...next]);
    setAnalysis(null); setCommitted(null);
  }, []);

  const removeFile = useCallback((id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    setAnalysis(null); setCommitted(null);
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]); setAnalysis(null); setCommitted(null); setProgress(0);
  }, []);

  // ── page-wide drag listeners ────────────────────────────────
  // Listening at window level so dropping anywhere on the page works.
  useEffect(() => {
    let dragDepth = 0;
    const onWinDragEnter = (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragDepth += 1;
      setDragOver(true);
    };
    const onWinDragOver = (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
    };
    const onWinDragLeave = (e) => {
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) setDragOver(false);
    };
    const onWinDrop = (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragDepth = 0;
      setDragOver(false);
      if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    };
    window.addEventListener('dragenter', onWinDragEnter);
    window.addEventListener('dragover',  onWinDragOver);
    window.addEventListener('dragleave', onWinDragLeave);
    window.addEventListener('drop',      onWinDrop);
    return () => {
      window.removeEventListener('dragenter', onWinDragEnter);
      window.removeEventListener('dragover',  onWinDragOver);
      window.removeEventListener('dragleave', onWinDragLeave);
      window.removeEventListener('drop',      onWinDrop);
    };
  }, [addFiles]);

  const browse = () => fileInputRef.current?.click();

  // ── upload + analyze / commit ───────────────────────────────
  const post = useCallback(async ({ commit, overwrite }) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f.file, f.name);
    fd.append('commit',    commit    ? 'true' : 'false');
    fd.append('overwrite', overwrite ? 'true' : 'false');
    setProgress(0);
    const { data, status } = await API.post(
      '/examination/score-sheet/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!e.total) return;
          setProgress(Math.round((e.loaded / e.total) * 100));
        },
        validateStatus: (s) => s < 500,
      }
    );
    setProgress(100);
    return { data, status };
  }, [files]);

  const analyze = async () => {
    if (files.length === 0) return swal.warning('Add at least one file');
    setBusy('analyzing');
    setAnalysis(null); setCommitted(null);
    try {
      const { data } = await post({ commit: false, overwrite: false });
      setAnalysis(data);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not analyze files'));
    } finally {
      setBusy(false);
    }
  };

  const importNow = async (overwrite = false) => {
    if (!analysis) return swal.warning('Analyze first');
    if (analysis.hasErrors) {
      return swal.warning('Some files have errors — fix them or remove them before importing');
    }
    if (analysis.hasConflict && !overwrite) {
      setConfirmOverwriteOpen(true); return;
    }
    setConfirmOverwriteOpen(false);
    setBusy('committing');
    setCommitted(null);
    try {
      const { data, status } = await post({ commit: true, overwrite });
      if (status === 409) {
        setAnalysis(data);
        setConfirmOverwriteOpen(true);
        return;
      }
      if (data?.ok) {
        setCommitted(data);
        swal.success(`Imported ${data.savedRows} student row(s) across ${data.totalSheets} sheet(s)`);
      } else {
        swal.error(data?.message || 'Import failed');
        setAnalysis(data);
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  // ── derived ─────────────────────────────────────────────────
  const totals = useMemo(() => {
    if (!analysis) return null;
    let sheets = 0, ok = 0, error = 0, conflict = 0, students = 0;
    for (const f of analysis.files || []) {
      for (const s of f.sheets || []) {
        sheets += 1;
        if (!s.ok) error += 1;
        if (s.conflict) conflict += 1;
        if (s.ok && !s.conflict) ok += 1;
        students += (s.students?.length || 0);
      }
    }
    return { sheets, ok, error, conflict, students };
  }, [analysis]);

  // ── render ──────────────────────────────────────────────────
  return (
    <Box sx={{
      maxWidth: 1300, mx: 'auto',
      px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 },
      // Soft gradient backdrop just for this page so the hero pops.
      minHeight: 'calc(100vh - 80px)',
    }}>
      <ModuleHeader
        icon={<RocketIcon />}
        title="Import Score Sheets"
        subtitle="Drop one or many Excel files anywhere on this page — the system handles class, term, exam type and academic year automatically."
      />

      {/* Workflow steps — visual breadcrumb */}
      <StepStrip current={currentStep} files={files} analysis={analysis} committed={committed} />

      {/* ── HERO DROP ZONE ── */}
      <Card sx={{
        mt: 2, p: { xs: 3, md: 5 },
        borderRadius: 4,
        position: 'relative',
        overflow: 'hidden',
        textAlign: 'center',
        border: '2px dashed',
        borderColor: 'primary.light',
        background: `
          radial-gradient(at 20% 20%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 50%),
          radial-gradient(at 80% 80%, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 50%),
          linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)
        `,
        backdropFilter: 'blur(8px)',
      }}>
        {/* Pulse rings under the cloud */}
        <Box sx={{
          position: 'relative', width: 110, height: 110, mx: 'auto', mb: 2,
        }}>
          {[0, 0.4, 0.8].map((delay, i) => (
            <Box key={i} sx={{
              position: 'absolute', inset: 0,
              borderRadius: '50%',
              border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              animation: `${pulseRing} 2.4s ${delay}s infinite ease-out`,
            }} />
          ))}
          <Box sx={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: '#fff',
            boxShadow: `0 14px 30px -10px ${alpha(theme.palette.primary.main, 0.6)}`,
            animation: `${floatAnim} 3.5s infinite ease-in-out`,
          }}>
            <CloudIcon sx={{ fontSize: 50 }} />
          </Box>
        </Box>

        <Typography sx={{
          fontWeight: 900, fontSize: { xs: 22, md: 30 }, mb: 0.6,
          color: 'primary.dark', letterSpacing: 0.3,
        }}>
          Drop Excel score sheets here
        </Typography>
        <Typography sx={{
          color: 'text.secondary', mb: 2.4, maxWidth: 540, mx: 'auto',
          fontSize: { xs: 13, md: 14.5 },
        }}>
          .xls · .xlsx · .xml — exported by the Raw Score Sheet.
          Drop many files at once, even from different classes — we'll
          read every sheet and validate before saving.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} justifyContent="center"
          alignItems="center">
          <Button variant="contained" size="large" startIcon={<FolderIcon />}
            onClick={browse}
            sx={{
              textTransform: 'none', fontWeight: 800, fontSize: 14.5,
              px: 3.4, py: 1.3, borderRadius: 99,
              boxShadow: `0 10px 24px -10px ${alpha(theme.palette.primary.main, 0.6)}`,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}>
            Browse files
          </Button>
          <Typography variant="caption" sx={{
            color: 'text.disabled', fontWeight: 700, letterSpacing: 1,
          }}>
            OR DROP ANYWHERE ON THIS PAGE
          </Typography>
        </Stack>

        <input type="file" hidden multiple
          accept={ACCEPTED.join(',')}
          ref={fileInputRef}
          onChange={(e) => addFiles(e.target.files)} />
      </Card>

      {/* ── QUEUE ── */}
      {files.length > 0 && (
        <Card sx={{ mt: 2, p: { xs: 1.6, md: 2.4 }, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center"
            spacing={1.4} sx={{ mb: 1.6 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PackageIcon />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: 16 }}>Queue</Typography>
                <Typography variant="caption" color="text.secondary">
                  {files.length} file{files.length === 1 ? '' : 's'} · {humanSize(files.reduce((s, f) => s + f.size, 0))}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Button variant="outlined" onClick={clearAll} disabled={!!busy}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 99 }}>
                Clear
              </Button>
              <Button startIcon={<AnalyzeIcon />} variant="outlined"
                onClick={analyze} disabled={!!busy || files.length === 0}
                sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
                {busy === 'analyzing' ? 'Analyzing…' : (analysis ? 'Re-analyze' : 'Analyze')}
              </Button>
              <Button startIcon={<RunIcon />} variant="contained"
                onClick={() => importNow(false)}
                disabled={!analysis || !!busy || analysis.hasErrors}
                sx={{
                  textTransform: 'none', fontWeight: 800, borderRadius: 99,
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  boxShadow: `0 8px 18px -8px ${alpha(theme.palette.success.main, 0.6)}`,
                  '&.Mui-disabled': { background: undefined, boxShadow: 'none' },
                }}>
                {busy === 'committing' ? 'Importing…' : 'Import all'}
              </Button>
            </Stack>
          </Stack>

          {!!busy && (
            <Box sx={{ mb: 1.5 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.6 }}>
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <MagicIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {busy === 'analyzing' ? 'Uploading & analyzing…' : 'Uploading & saving…'}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ fontWeight: 900, color: 'primary.main' }}>
                  {progress}%
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={progress}
                sx={{
                  height: 8, borderRadius: 99,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 99,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  },
                }} />
            </Box>
          )}

          <Grid container spacing={1.2}>
            {files.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={f.id}>
                <Grow in timeout={200 + i * 60}>
                  <Stack direction="row" alignItems="center" spacing={1.2}
                    sx={{
                      px: 1.4, py: 1.1, borderRadius: 2,
                      border: '1px solid', borderColor: 'divider',
                      bgcolor: 'rgba(255,255,255,0.85)',
                      transition: 'all 160ms ease',
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: '0 4px 12px -6px rgba(0,0,0,0.15)',
                      },
                    }}>
                    <Box sx={{
                      width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: 'success.dark',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <SheetIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontWeight: 700, fontSize: 13.5, lineHeight: 1.2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {f.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {humanSize(f.size)}
                      </Typography>
                    </Box>
                    <IconButton size="small" color="error"
                      onClick={() => removeFile(f.id)} disabled={!!busy}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Card>
      )}

      {/* ── ANALYSIS SUMMARY TILES ── */}
      {analysis && totals && (
        <Grid container spacing={1.4} sx={{ mt: 0.6 }}>
          <Grid item xs={6} sm={3}>
            <SummaryTile icon={<SheetIcon />} label="Sheets"
              value={totals.sheets} tint={theme.palette.primary.main} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryTile icon={<DoneIcon />} label="Ready"
              value={totals.ok} tint={theme.palette.success.main} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryTile icon={<WarnIcon />} label="Already imported"
              value={totals.conflict} tint={theme.palette.warning.main} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryTile icon={<StudentsIcon />} label="Student rows"
              value={totals.students} tint={theme.palette.secondary.main} />
          </Grid>
        </Grid>
      )}

      {/* ── ANALYSIS RESULTS ── */}
      {analysis && (
        <Card sx={{ mt: 1.6, p: 2, borderRadius: 3 }}>
          {committed?.ok && (
            <Alert severity="success" variant="filled" sx={{ mb: 1.5, borderRadius: 2 }}
              icon={<RocketIcon />}>
              <Typography fontWeight={800}>
                Imported {committed.savedRows} student row{committed.savedRows === 1 ? '' : 's'} across
                {' '}{committed.totalSheets} sheet{committed.totalSheets === 1 ? '' : 's'}.
              </Typography>
            </Alert>
          )}

          {!committed && analysis.hasErrors && (
            <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} icon={<ErrorIcon />}>
              Some sheets have validation errors. Fix them in the Excel
              file (or remove the file from the queue) before importing.
            </Alert>
          )}

          {!committed && !analysis.hasErrors && analysis.hasConflict && (
            <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }} icon={<WarnIcon />}>
              Some sheets have already been imported. Clicking <b>Import all</b>
              {' '}will prompt you to confirm before overwriting.
            </Alert>
          )}

          {analysis.files.map((file, fi) => (
            <FileReport key={fi} file={file} />
          ))}
        </Card>
      )}

      {/* ── PAGE-WIDE DRAG OVERLAY ── */}
      <Fade in={dragOver} timeout={150}>
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 2000,
          bgcolor: alpha(theme.palette.primary.main, 0.18),
          backdropFilter: 'blur(8px)',
          border: `4px dashed ${theme.palette.primary.main}`,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <CloudIcon sx={{ fontSize: 120, color: 'primary.main', mb: 2,
            filter: 'drop-shadow(0 6px 18px rgba(26,35,126,0.4))' }} />
          <Typography sx={{
            fontWeight: 900, fontSize: 32, color: 'primary.dark',
            textShadow: '0 2px 6px rgba(255,255,255,0.8)',
          }}>
            Drop to import
          </Typography>
        </Box>
      </Fade>

      {/* ── OVERWRITE CONFIRMATION ── */}
      <Dialog open={confirmOverwriteOpen} onClose={() => !busy && setConfirmOverwriteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
          <WarnIcon color="warning" />
          Already imported — overwrite?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <b>{analysis?.files
              ?.flatMap(f => f.sheets.filter(s => s.conflict))
              .length}</b> sheet(s) already exist in the database for this
            (class · term · exam · year). Importing will <b>replace</b>
            the existing scores with the ones in the Excel file.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is permanent — old scores cannot be recovered unless
            you have the previous Excel export.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOverwriteOpen(false)} disabled={!!busy}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button variant="contained" color="warning"
            onClick={() => importNow(true)} disabled={!!busy}
            startIcon={busy ? <CircularProgress size={16} /> : <RedoIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99, px: 3 }}>
            {busy === 'committing' ? 'Overwriting…' : 'Yes, overwrite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Step indicator strip ─────────────────────────────────────
function StepStrip({ current, files, analysis, committed }) {
  const theme = useTheme();
  const steps = [
    { n: 1, label: 'Drop files',
      sub: files.length > 0 ? `${files.length} file(s) queued` : 'browse or drop here',
      icon: <FolderIcon /> },
    { n: 2, label: 'Analyze',
      sub: analysis
        ? (analysis.hasErrors ? 'errors found — review' : 'review the report')
        : 'click Analyze to validate',
      icon: <AnalyzeIcon /> },
    { n: 3, label: 'Import',
      sub: committed?.ok ? `${committed.savedRows} rows saved` : 'commit to database',
      icon: <RocketIcon /> },
  ];
  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
        {steps.map((s) => {
          const active = current === s.n;
          const done = current > s.n;
          const tint = done
            ? theme.palette.success.main
            : active ? theme.palette.primary.main : theme.palette.text.disabled;
          return (
            <Stack key={s.n} direction="row" alignItems="center" spacing={1.4}
              sx={{
                flex: 1, p: 1.4, borderRadius: 2,
                border: '1px solid', borderColor: active ? 'primary.main' : 'divider',
                bgcolor: active
                  ? alpha(theme.palette.primary.main, 0.06)
                  : 'rgba(255,255,255,0.7)',
                transition: 'all 160ms ease',
              }}>
              <Box sx={{
                width: 42, height: 42, borderRadius: '50%',
                bgcolor: alpha(tint, 0.15),
                color: tint,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900,
                position: 'relative',
              }}>
                {done ? <DoneIcon /> : s.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{
                  fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
                  textTransform: 'uppercase', color: 'text.secondary',
                }}>
                  Step {s.n}
                </Typography>
                <Typography sx={{ fontWeight: 800, color: active ? 'primary.dark' : 'text.primary' }}>
                  {s.label}
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.sub}
                </Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

// ── Summary tile ─────────────────────────────────────────────
function SummaryTile({ icon, label, value, tint }) {
  return (
    <Card sx={{
      p: 1.6, borderRadius: 3,
      border: '1px solid', borderColor: 'divider',
      bgcolor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(8px)',
    }}>
      <Stack direction="row" alignItems="center" spacing={1.4}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          bgcolor: alpha(tint, 0.12), color: tint,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{
            fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5,
            textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1,
          }}>
            {label}
          </Typography>
          <Typography sx={{
            fontWeight: 900, fontSize: 22, color: tint, mt: 0.4, lineHeight: 1.1,
          }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

// ── per-file collapsible report card ─────────────────────────
function FileReport({ file }) {
  const theme = useTheme();
  const fileTint = file.ok
    ? theme.palette.success.main
    : theme.palette.error.main;
  const headerBg = file.ok
    ? alpha(theme.palette.success.main, 0.08)
    : alpha(theme.palette.error.main, 0.08);

  return (
    <Accordion defaultExpanded={!file.ok || file.sheets?.some(s => s.conflict)}
      elevation={0}
      sx={{
        mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
        '&:before': { display: 'none' },
        overflow: 'hidden',
      }}>
      <AccordionSummary expandIcon={<ExpandIcon />} sx={{ bgcolor: headerBg }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            bgcolor: alpha(fileTint, 0.16), color: fileTint,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <SheetIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} sx={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {file.sheets?.length || 0} sheet(s)
              {file.error ? ` · error: ${file.error}` : ''}
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {file.error && (
          <Alert severity="error" sx={{ mb: 1.2, mt: 1, borderRadius: 1.5 }}>{file.error}</Alert>
        )}
        {(file.sheets || []).map((sheet, si) => (
          <SheetReport key={si} sheet={sheet} />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

// ── per-sheet detail strip ───────────────────────────────────
function SheetReport({ sheet }) {
  const theme = useTheme();
  const isError = !sheet.ok;
  const isConflict = sheet.conflict;
  const tint = isError
    ? theme.palette.error.main
    : isConflict
      ? theme.palette.warning.main
      : theme.palette.success.main;
  const headerBg = alpha(tint, 0.08);

  return (
    <Box sx={{
      mt: 1.2, border: '1px solid', borderColor: alpha(tint, 0.3), borderRadius: 2,
      bgcolor: 'rgba(255,255,255,0.6)', overflow: 'hidden',
    }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.4, py: 1.1, bgcolor: headerBg }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '50%',
          bgcolor: alpha(tint, 0.18), color: tint,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isError
            ? <ErrorIcon sx={{ fontSize: 18 }} />
            : isConflict
              ? <WarnIcon sx={{ fontSize: 18 }} />
              : <DoneIcon sx={{ fontSize: 18 }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={800} sx={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {sheet.bucket?.sub || sheet.worksheet_name || '(unknown subject)'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {sheet.bucket
              ? `${sheet.bucket.cla || '?'} · ${(sheet.bucket.term || '').replace(/_/g, ' ')} · ${sheet.bucket.state || '?'} · ${sheet.bucket.acc_yer || '?'}`
              : sheet.worksheet_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.6}>
          <Chip size="small" variant="outlined"
            label={`${sheet.students?.length || 0} student(s)`}
            sx={{ fontWeight: 700, height: 24 }} />
          {isConflict && (
            <Tooltip title={`${sheet.existing_count} row(s) already in the database for this bucket`}>
              <Chip size="small" color="warning"
                label={`exists (${sheet.existing_count})`}
                sx={{ fontWeight: 700, height: 24 }} />
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {sheet.error && (
        <Alert severity="error" sx={{ m: 1.2, borderRadius: 1.5 }}>{sheet.error}</Alert>
      )}

      {sheet.errors && sheet.errors.length > 0 && (
        <Box sx={{ p: 1.2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 12.5,
            color: 'error.main', mb: 0.8 }}>
            {sheet.errors.length} issue(s) detected:
          </Typography>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 800, py: 0.5 }}>Index</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.5 }}>Row</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.5 }}>Column</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.5 }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 800, py: 0.5 }}>Issue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sheet.errors.slice(0, 50).map((e, ei) => (
                  <TableRow key={ei} hover>
                    <TableCell sx={{ py: 0.4, fontFamily: 'monospace' }}>{e.ind || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>{e.row || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>{e.column || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4 }}>{e.value || '—'}</TableCell>
                    <TableCell sx={{ py: 0.4, color: 'error.main', fontWeight: 700 }}>
                      {e.issue}
                    </TableCell>
                  </TableRow>
                ))}
                {sheet.errors.length > 50 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ fontStyle: 'italic', py: 0.6 }}>
                      and {sheet.errors.length - 50} more…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
